import app from "@/main";
import {getApiBaseUrl, apiFetch} from "@/_helpers";

export const configs = {
    namespaced: true,
    state: {
        appName: null,
        loyaltyProgramName: null,
        highlightsMenu: false,
        storesMenu: false,
        offersMenu: false,
        purchasesMenu: false,
        helpMenu: false,
        termsConditionsMenu: false,
        qrcodeMenu: false,
        supportEmailAddress: null,
        supportExternalLink: null,
        supportSubject: null,
        supportIntroText: null,
        loginProviderFacebook: null,
        loginProviderInstagram: null,
        loginProviderGoogle: null,
        socialNetworkFacebook: null,
        socialNetworkInstagram: null,
        offersGenericMessage: null,
        countryCode: null,
        preferredCountryCodes: null,
        shopAvailable: false,
        recaptchaAvailable: true,
        currencySymbol: null,
        apiImgBaseUrl: null,
        generalConfigInit: false,
    },
    getters: {
        appName: state => state.appName,
        loyaltyProgramName: state => state.loyaltyProgramName,
        highlightsMenu: state => state.highlightsMenu,
        storesMenu: state => state.storesMenu,
        offersMenu: state => state.offersMenu,
        purchasesMenu: state => state.purchasesMenu,
        helpMenu: state => state.helpMenu,
        // @modified ana.castro 2019.12.30 SCLAPP-23
        termsConditionsMenu: state => state.termsConditionsMenu,
        // @modified ana.castro 2019.09.30 SCLAPP-22
        qrcodeMenu: state => state.qrcodeMenu,
        supportEmailAddress: state => state.supportEmailAddress,
        // @modified ana.castro 2019.12.30 SCLAPP-23
        supportExternalLink: state => state.supportExternalLink,
        supportSubject: state => state.supportSubject,
        supportIntroText: state => state.supportIntroText,
        // @modified ana.castro 2019.10.28 Sometimes the Social Provider definitions were not available to be used on the mounted() section of the LogInUser
        loginProviderFacebook: state => state.loginProviderFacebook,
        loginProviderInstagram: state => state.loginProviderInstagram,
        loginProviderGoogle: state => state.loginProviderGoogle,
        // @modified ana.castro 2019.12.19 SCLAPP-23 Display a URL to the Social Networks (FB and IG)
        socialNetworkFacebook: state => state.socialNetworkFacebook,
        socialNetworkInstagram: state => state.socialNetworkInstagram,
        // @modified ana.castro 2020.05.11 SCLAPP-25 Offers' Message
        offersGenericMessage: state => state.offersGenericMessage,
        //@modified ana.castro 2020.05.12 SCLAPP-28 The Country Code should be appended to the Phone Number
        countryCode: state => state.countryCode,
        preferredCountryCodes: state => state.preferredCountryCodes,
        // @modified ana.castro 2020.10.14 SCLAPP-33
        shopAvailable: state => state.shopAvailable,
        recaptchaAvailable: state => state.recaptchaAvailable,
        currencySymbol: state => state.currencySymbol,
        apiImgBaseUrl: state => state.apiImgBaseUrl,
        generalConfigInit: state => state.generalConfigInit,
    },
    actions: {
        setGeneralConfig({commit}, settings) {
            commit('setGeneralConfig', settings);
        },

        async initGeneralConfig({commit, state}) {
            // Get general config only once: called from the router
            if(!state.init) {
                try {
                    const generalConfig = await apiFetch('getgenericdefinitions/');
                    commit('setGeneralConfig', generalConfig);
                    commit('setGeneralConfigInit');
                } catch (e) {
                    app.$log.error(e);
                }
            }
      }
    },
    mutations: {
        /**
         * This function defines a group of general attributes for the application.
         * This definition is obtained by calling the DIMAS Url api-clapp-get-generic-definitions.
         * @param state
         * @param settings The expected settings' structure is a dictionary:
         *           app_name: xx
         *           loyalty_program_name: xx
         *           support:
         *              emailAddress: xx
         *              subject: xx
         *              text: xx
         *          menus:
         *              helpMenu: 0
         *              termsConditionsMenu: 0
         *              highlightsMenu: 0
         *              offersMenu: 1
         *              purchasesMenu: 1
         *              storesMenu: 1
         *              qrcodeMenu: 1
         *          socialNetwork:
         *              facebook: 1
         *              instagram: 1
         *              google: 1
         *          offers:
         *              message: xx
         *          phone_country_code: 351
         *          preferred-countries: ["PT","GB"]
         *          shopAvailable: 1
         *          currency: "€"
         */
        setGeneralConfig(state, settings) {
            //App Identification
            state.appName = settings['app_name'];
            state.loyaltyProgramName = settings['loyalty_program_name'];

            //Menus
            state.highlightsMenu = settings['menus']['highlight']['status'];
            state.storesMenu = settings['menus']['store']['status'];
            state.offersMenu = settings['menus']['offer']['status'];
            state.purchasesMenu = settings['menus']['purchase']['status'];
            state.helpMenu = settings['menus']['help']['status'];
            // @modified ana.castro 2019.12.30 SCLAPP-23
            state.termsConditionsMenu = settings['menus']['termsConditions']['status'];
            // @modified ana.castro 2019.09.30 SCLAPP-22
            // state.qrcodeMenu = settings['menus']['qrcode']['status'];
            if (settings['menus']['qrcode']['status'] === 1) {
                state.qrcodeMenu = true;
            }

            // Support Definition
            if (state.helpMenu === 1) {
                state.supportEmailAddress = settings['support']['emailAddress'];
                // @modified ana.castro 2019.12.30 SCLAPP-23
                state.supportExternalLink = settings['support']['externalLink'];
                state.supportSubject = settings['support']['subject'];
                state.supportIntroText = settings['support']['text'];
            }

            if (settings['providers']) {
                state.loginProviderFacebook = settings['providers']['FB']['status'];
                state.loginProviderInstagram = settings['providers']['IG']['status'];
                state.loginProviderGoogle = settings['providers']['GOOGLE']['status'];
            } else {
                state.loginProviderFacebook = false;
                state.loginProviderInstagram = false;
                state.loginProviderGoogle = false;
            }

            // @modified ana.castro 2019.12.19 SCLAPP-23 Display a URL to the Social Networks (FB and IG)
            state.socialNetworkFacebook = settings['socialNetwork']['FB'];
            state.socialNetworkInstagram = settings['socialNetwork']['IG'];

            //@modified ana.castro 2020.05.11 SCLAPP-25 Offers' Message
            state.offersGenericMessage = settings['offers']['message'];

            //@modified ana.castro 2020.05.12 SCLAPP-28 The Country Code should be appended to the Phone Number
            state.countryCode = settings['phone_country_code'];
            state.preferredCountryCodes = settings['preferred-countries'];

            // @modified ana.castro 2020.10.14 SCLAPP-33
            if (settings['shopAvailable'] === 1) {
                state.shopAvailable = true;
            }
            state.currencySymbol = settings['currency'];
            state.apiImgBaseUrl = getApiBaseUrl() + "clapp/getimage/?image=";

            app.$log.debug("Set General Config | State: " + JSON.stringify(state));
        },
        setGeneralConfigInit(state) {
            state.generalConfigInit = true
        },
    }
};
